import {
  SessionStorage,
  StorageKey,
} from '@internal/utils/storage/SessionStorage'
import { FittedImage } from '@renderer-ui-library/atoms'
import {
  MachineMedia,
  ThumbnailSlider,
  getMachineImageAlt,
} from '@renderer-ui-library/molecules'
import MachineAlertItaly from '@renderer-ui-library/molecules/MachineAlertItaly/MachineAlertItaly'
import { useIsNotRegisterableInItaly } from '@renderer-ui-library/molecules/MachineAlertItaly/useIsNotRegisterableInItaly'
import { Grid } from '@renderer-ui-library/mui'
import { ImageRatio } from '@renderer-ui-library/mui/utils/media/ImageRatio'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import { ContactWizardContext } from '@renderer-ui-library/organisms'
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { MachinePlaceholderImageMapper } from 'utils/machine/placeholder/MachinePlaceholderImageMapper'
import { useMachineTypeName } from 'utils/machine/useMachineTypeName'
import { SalesManagerContext } from 'utils/SalesManager/SalesManagerContext'
import { BlockProps } from '../BlockProps'
import { IMachineOverviewBlockData } from './IMachineOverviewBlockData'
import styles from './machineOverviewBlock.module.scss'
import { MachineOverviewBreadcrumb } from './machineOverviewBreadcrumb'
import { MachineOverviewSidebar } from './machineOverviewSidebar'
import { USPs } from './USPs/USPs'

const SCROLL_THRESHOLD_PERCENT = 40

export const MachineOverviewBlock: React.FC<
  BlockProps<IMachineOverviewBlockData>
> = React.memo((props) => {
  const pdpMeta = props.pagePropsMetaData.pdp
  const machine = pdpMeta?.machine
  const { isMobile } = useMediaQuery()
  const { openCallBackOption } = useContext(ContactWizardContext)
  const { salesManagers } = useContext(SalesManagerContext)
  const { getMachineTypeName } = useMachineTypeName()
  const isNotRegisterableInItaly = useIsNotRegisterableInItaly()
  const hasImages = !!machine?.media.length

  const sessionStorage = useMemo(
    () => new SessionStorage(StorageKey.PdpCallBack),
    []
  )

  const callbackPopupEventFired = useCallback(() => {
    if (!sessionStorage.isSet() && props.locale && salesManagers) {
      openCallBackOption('callback-form-popup')
    }
  }, [openCallBackOption, props.locale, salesManagers, sessionStorage])

  const callEventScrollFired = useCallback(() => {
    if (sessionStorage.expired() && props.locale) {
      const scrollY = window.scrollY
      const clientHeight = document.body.clientHeight
      const scrolledBy = (100 * scrollY) / clientHeight

      if (Math.floor(scrolledBy) > SCROLL_THRESHOLD_PERCENT) {
        callbackPopupEventFired()
        sessionStorage.store()
      }
    }
  }, [callbackPopupEventFired, props.locale, sessionStorage])

  useEffect(() => {
    document.addEventListener('scroll', callEventScrollFired)
    return () => {
      document.removeEventListener('scroll', callEventScrollFired)
    }
  }, [callEventScrollFired])

  const media = useMemo(
    () =>
      machine?.media.map(
        (media): MachineMedia => ({
          ...media,
          alternativeText: getMachineImageAlt(
            machine.name,
            machine.machineType,
            machine.price,
            props.locale
          ),
        })
      ) ?? [],
    [machine, props.locale]
  )

  if (!pdpMeta || !machine) {
    return null
  }

  return (
    <BlockWrapper blockData={props.data} noSpacingTop noSpacingBottom>
      <div className={styles.wrapper}>
        {!isMobile && (
          <MachineOverviewBreadcrumb
            locale={props.locale}
            machine={machine}
            machineCategoryPages={pdpMeta.machineCategoryPages}
          />
        )}

        <Grid container rowSpacing={0} columnSpacing={4}>
          <Grid item xs={12} md={7} paddingX={{ xs: 0, md: 0 }}>
            {isNotRegisterableInItaly && <MachineAlertItaly />}
            <div className={styles.mediaWrapper}>
              {hasImages ? (
                <ThumbnailSlider
                  machine={machine}
                  locale={props.locale}
                  imageRatio={ImageRatio.Landscape}
                  media={media}
                  slidesWrapperClassName={styles.imageWrapper}
                  hasMediaOverlay
                  pagination
                />
              ) : (
                <div className={styles.imageWrapper}>
                  <FittedImage
                    ratio={ImageRatio.Landscape}
                    src={
                      MachinePlaceholderImageMapper(machine.machineType, 'icon')
                        .url
                    }
                    alt={getMachineTypeName(machine.machineType, 'singular')}
                  />
                </div>
              )}
              {!isMobile && props.data.services.length > 0 && (
                <div className={styles.uspsWrapper}>
                  <USPs services={props.data.services} />
                </div>
              )}
            </div>
            {isMobile && (
              <MachineOverviewBreadcrumb
                hideBackButton
                locale={props.locale}
                machine={machine}
                machineCategoryPages={pdpMeta.machineCategoryPages}
              />
            )}
          </Grid>
          <Grid item xs={12} md={5} paddingX={{ xs: 0, md: 0 }}>
            <MachineOverviewSidebar
              locale={props.locale}
              salesManagers={salesManagers}
              services={props.data.services}
              machine={machine}
              displayDefinition={
                pdpMeta.displayDefinitions.WEBSITE_MACHINE_OVERVIEW ?? null
              }
            />
          </Grid>
        </Grid>
      </div>
    </BlockWrapper>
  )
})

MachineOverviewBlock.displayName = 'MachineOverviewBlock'
